/**
 * TrustPostureHistory.jsx — Phase 12.9 · 30/90-day uptime sparklines.
 *
 * Renders one heatmap row per defence layer + an "Overall" row.
 * Each cell is one UTC day, coloured green / yellow / red / unknown
 * based on the snapshot taken by the daily 02:00 UTC cron. Hovering
 * a cell surfaces the day_key + status (no internal detail leaks).
 *
 * Sits below `TrustLivePostureTile` on the Trust Center page.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  GitMerge, Archive, RotateCcw, Network, Mail, Package,
  AlertTriangle, Server, CreditCard, ShieldCheck, RefreshCw,
} from "lucide-react";


const API = process.env.REACT_APP_BACKEND_URL;
const WINDOWS = [7, 30, 90];

const LAYER_META = {
  audit_chain:     { Icon: GitMerge,        title: "Audit chain"          },
  r2_archive:      { Icon: Archive,         title: "Cold archive"         },
  backup_drill:    { Icon: RotateCcw,       title: "Backup drill"         },
  subdomain_sweep: { Icon: Network,         title: "Subdomain sweep"      },
  email_posture:   { Icon: Mail,            title: "Email auth"           },
  supply_chain:    { Icon: Package,         title: "Supply chain"         },
  under_attack:    { Icon: AlertTriangle,   title: "Edge defence"         },
  waf_sync:        { Icon: Server,          title: "WAF sync"             },
  stripe_live:     { Icon: CreditCard,      title: "Stripe live"          },
};

const STATUS_COLOR = {
  green:   "#1FDD83",
  yellow:  "#FFB000",
  red:     "#FF6E6E",
  unknown: "#3A4150",
};


export default function TrustPostureHistory() {
  const [window, setWindow] = useState(30);
  const [data, setData]     = useState(null);
  const [error, setError]   = useState("");

  const fetchHistory = async (w = window) => {
    try {
      const { data } = await axios.get(
        `${API}/api/public/trust/posture/history?window=${w}`,
      );
      setData(data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load posture history.");
    }
  };

  useEffect(() => { fetchHistory(window); }, [window]);     // eslint-disable-line

  if (error) {
    return (
      <div
        className="rounded-md border border-rose-700 bg-rose-900/30 p-5 text-sm text-rose-200"
        data-testid="trust-posture-history-error"
      >
        <AlertTriangle className="mr-2 inline h-4 w-4" />
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className="rounded-md border border-gray-800 bg-[#0E1219] p-6 text-sm text-gray-400"
        data-testid="trust-posture-history-loading"
      >
        Loading uptime trail…
      </div>
    );
  }

  const layers = data.layers || [];
  const uptimePct = data.summary?.uptime_pct ?? 0;
  const headlineColor =
    uptimePct >= 95 ? STATUS_COLOR.green :
    uptimePct >= 80 ? STATUS_COLOR.yellow :
                       STATUS_COLOR.red;

  return (
    <section
      className="rounded-md border border-[#1FDD83]/30 bg-[#0E1219] p-6"
      data-testid="trust-posture-history"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#1FDD83]">
            <ShieldCheck className="h-3.5 w-3.5" />
            Posture history
            <span className="text-gray-500 normal-case">·</span>
            <span
              className="font-mono text-gray-500"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
            >
              {data.policy_version}
            </span>
          </div>
          <div className="flex items-baseline gap-3">
            <span
              className="font-mono text-3xl font-bold"
              style={{ color: headlineColor, fontFamily: '"IBM Plex Mono", monospace' }}
              data-testid="trust-posture-history-uptime"
            >
              {uptimePct}%
            </span>
            <span className="text-xs text-gray-500">
              all-green uptime over the last {window} days
              {" · "}
              {data.summary?.green_days ?? 0}d green ·{" "}
              {data.summary?.yellow_days ?? 0}d yellow ·{" "}
              {data.summary?.red_days ?? 0}d red ·{" "}
              {data.summary?.unknown_days ?? 0}d unknown
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="inline-flex overflow-hidden rounded-md border border-white/10"
            data-testid="trust-posture-history-windows"
            role="group"
          >
            {WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWindow(w)}
                className="border-r border-white/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest last:border-r-0 transition-colors"
                style={{
                  background: window === w ? "rgba(31,221,131,0.18)" : "transparent",
                  color:      window === w ? "#1FDD83" : "#9CA3AF",
                }}
                data-testid={`trust-posture-history-window-${w}`}
              >
                {w}d
              </button>
            ))}
          </div>
          <button
            onClick={() => fetchHistory(window)}
            className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-transparent px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-gray-400 transition-colors hover:border-white/30 hover:text-white"
            data-testid="trust-posture-history-refresh"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Overall trace */}
      <HeatmapRow
        title="Overall"
        Icon={ShieldCheck}
        series={data.series_overall || []}
        uptimePct={uptimePct}
        emphasis
        testid="trust-posture-history-overall"
      />

      <div className="my-3 h-px bg-white/5" />

      <ul
        className="space-y-1"
        data-testid="trust-posture-history-rows"
      >
        {layers.map((layer) => {
          const meta   = LAYER_META[layer] || { Icon: ShieldCheck, title: layer };
          const series = data.series_by_layer?.[layer] || [];
          const u      = data.summary?.per_layer?.[layer] || {};
          return (
            <HeatmapRow
              key={layer}
              title={meta.title}
              Icon={meta.Icon}
              series={series}
              uptimePct={u.green_pct ?? 0}
              testid={`trust-posture-history-layer-${layer}`}
            />
          );
        })}
      </ul>

      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] text-gray-500">
        {Object.entries(STATUS_COLOR).map(([k, c]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ background: c }}
            />
            {k}
          </span>
        ))}
        <span className="ml-auto">
          {data.samples ?? 0} snapshots · window {window}d
        </span>
      </div>
    </section>
  );
}


/* ─── HeatmapRow ──────────────────────────────────────────────── */

function HeatmapRow({ title, Icon, series, uptimePct, emphasis, testid }) {
  return (
    <div
      className="flex items-center gap-3"
      data-testid={testid}
    >
      <div className="flex w-36 items-center gap-2 flex-shrink-0">
        <Icon className="h-3.5 w-3.5 text-gray-500" />
        <span
          className={emphasis ? "text-sm font-bold text-white" : "text-xs text-gray-300"}
        >
          {title}
        </span>
      </div>
      <div className="flex flex-1 items-center gap-[2px] overflow-x-auto py-1">
        {series.map((s) => (
          <span
            key={s.day_key}
            title={`${s.day_key} · ${s.status}`}
            className="inline-block flex-shrink-0 rounded-[2px]"
            style={{
              width:      emphasis ? 8 : 6,
              height:     emphasis ? 16 : 12,
              background: STATUS_COLOR[s.status] || STATUS_COLOR.unknown,
              opacity:    s.status === "unknown" ? 0.6 : 1,
            }}
          />
        ))}
      </div>
      <span
        className="w-14 flex-shrink-0 text-right font-mono text-[11px] text-gray-400"
        style={{ fontFamily: '"IBM Plex Mono", monospace' }}
      >
        {Math.round(uptimePct)}%
      </span>
    </div>
  );
}
